import { track } from "./analytics";

interface LoadMoreConfig {
    gridSelector: string;
    itemSelector: string;
    buttonSelector: string;
}

class LoadMore {
    private _config: LoadMoreConfig;
    private _button: HTMLButtonElement;
    private _grid: HTMLElement;
    private _isLoading: boolean;

    constructor(config: LoadMoreConfig) {
        this._config = config;
    }

    init() {
        this._grid = document.querySelector(this._config.gridSelector);
        this._button = document.querySelector(this._config.buttonSelector);

        if (this._button) {
            this._button.addEventListener("click", () => this.loadNextPage());
        }
    }

    private buildPageURL(page: number): string {
        let path = window.location.pathname.replace(/page\/\d+\/?$/, "");

        if (!path.endsWith("/")) {
            path += "/";
        }

        return `${path}page/${page}/`;
    }

    private loadNextPage() {
        if (!this._grid || !this._button || this._isLoading) {
            return;
        }

        const currentPage = parseInt(this._grid.getAttribute("data-current-page") || "1", 10);
        const totalPages = parseInt(this._grid.getAttribute("data-total-pages") || "1", 10);
        const nextPage = currentPage + 1;

        if (nextPage > totalPages) {
            return;
        }

        this._isLoading = true;
        this._button.disabled = true;

        fetch(this.buildPageURL(nextPage))
            .then((response) => response.text())
            .then((html) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");
                const nextGrid = doc.querySelector(this._config.gridSelector);
                const loadMoreWrap = this._button.parentElement;

                if (!nextGrid || !loadMoreWrap) {
                    return;
                }

                const items = nextGrid.querySelectorAll(`:scope > ${this._config.itemSelector}`);

                items.forEach((item) => {
                    this._grid.insertBefore(item, loadMoreWrap);
                });

                // Initialise any AdSense units that arrived in this batch.
                // Inline <script> tags inside dynamically moved DOM nodes don't
                // auto-execute (browser security), so we call push() manually
                // for each .adsbygoogle element that hasn't been filled yet.
                const adsbygoogle = (window as any).adsbygoogle;
                if (adsbygoogle) {
                    this._grid
                        .querySelectorAll<HTMLElement>(".adsbygoogle:not([data-adsbygoogle-status])")
                        .forEach(() => adsbygoogle.push({}));
                }

                this._grid.setAttribute("data-current-page", String(nextPage));
                track("load_more", { page_number: nextPage, feed: this._config.gridSelector === "#tile-grid" ? "tiles" : "cards" });

                if (nextPage >= totalPages) {
                    loadMoreWrap.remove();
                }
            })
            .finally(() => {
                this._isLoading = false;
                if (this._button) {
                    this._button.disabled = false;
                }
            });
    }
}

export default LoadMore;
